import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, LogOut, Inbox, MessageSquare, Trash2, RefreshCw, Mail } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Logo } from '@/components/Logo';
import PageHead from '@/components/PageHead';
import { toast } from 'sonner';
import { adminListQuotes, adminListContacts, adminStats, adminDeleteContact, adminDeleteQuote } from '@/lib/api';

const STATUS_LABEL = {
  new: 'Nouveau',
  in_progress: 'En cours',
  replied: 'Répondu',
  archived: 'Archivé',
};

const STATUS_COLOR = {
  new: 'bg-[hsl(186_45%_92%)] text-[hsl(186_55%_28%)] border-[hsl(186_45%_70%)]',
  in_progress: 'bg-amber-50 text-amber-800 border-amber-200',
  replied: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  archived: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const adminEmail = localStorage.getItem('alofisa_admin_email') || '';

  const reload = async () => {
    setLoading(true);
    try {
      const [q, c, s] = await Promise.all([adminListQuotes(statusFilter), adminListContacts(), adminStats()]);
      setQuotes(q);
      setContacts(c);
      setStats(s);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [statusFilter]);

  const filteredQuotes = useMemo(() => {
    if (!search) return quotes;
    const q = search.toLowerCase();
    return quotes.filter((x) => [x.name, x.email, x.company, x.product_name, x.message].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [quotes, search]);

  const logout = () => {
    localStorage.removeItem('alofisa_token');
    localStorage.removeItem('alofisa_admin_email');
    navigate('/admin/login');
  };

  const handleDeleteQuote = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    await adminDeleteQuote(id);
    toast.success('Demande supprimée');
    reload();
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    await adminDeleteContact(id);
    toast.success('Message supprimé');
    reload();
  };

  const chartData = stats ? [
    { name: 'Nouveau', value: stats.new_quotes },
    { name: 'En cours', value: stats.in_progress_quotes },
    { name: 'Répondu', value: stats.replied_quotes },
    { name: 'Archivé', value: stats.archived_quotes },
  ] : [];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard-page">
      <PageHead title="Tableau de bord admin" />
      <header className="sticky top-0 z-30 bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/admin"><Logo size="sm" /></Link>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="hidden sm:inline">Connecté ·</span>
            <span className="font-medium text-foreground" data-testid="admin-current-email">{adminEmail}</span>
            <Button variant="ghost" size="sm" onClick={reload} className="ml-2" aria-label="Recharger"><RefreshCw className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-button"><LogOut className="w-4 h-4 mr-1" />Déconnexion</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Tableau de bord</h1>
        <p className="text-sm text-muted-foreground">Suivi des demandes de devis et messages reçus via le site.</p>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { k: 'total_quotes', label: 'Demandes (total)', icon: Inbox },
            { k: 'new_quotes', label: 'Nouvelles', icon: Inbox },
            { k: 'replied_quotes', label: 'Répondues', icon: Mail },
            { k: 'contact_messages', label: 'Messages contact', icon: MessageSquare },
          ].map(({ k, label, icon: Icon }) => (
            <Card key={k} className="p-5 rounded-2xl border-border">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
                  <div className="font-heading text-3xl font-semibold mt-1" data-testid={`stat-${k}`}>{stats ? stats[k] : '—'}</div>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[hsl(186_55%_38%)]" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-6">
          <Card className="p-5 rounded-2xl border-border">
            <h2 className="font-heading font-semibold">Répartition des demandes</h2>
            <div className="mt-3 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#475569" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#475569" allowDecimals={false} />
                  <Tooltip cursor={{ fill: 'rgba(31,122,122,0.08)' }} />
                  <Bar dataKey="value" fill="hsl(186 55% 38%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <div className="mt-8">
          <Tabs defaultValue="quotes">
            <TabsList>
              <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Demandes de devis</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Messages contact</TabsTrigger>
            </TabsList>

            <TabsContent value="quotes">
              <Card className="mt-4 rounded-2xl border-border overflow-hidden">
                <div className="p-4 flex flex-col md:flex-row md:items-center gap-3 border-b border-border">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, email, société, produit)" className="pl-9 rounded-xl" data-testid="admin-search-input" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {['all', 'new', 'in_progress', 'replied', 'archived'].map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-white border-border hover:border-primary'}`}
                        data-testid={`admin-filter-${s}`}
                      >
                        {s === 'all' ? 'Tous' : STATUS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                </div>

                {loading ? (
                  <div className="p-6 space-y-3">
                    {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : filteredQuotes.length === 0 ? (
                  <div className="p-12 text-center">
                    <h3 className="font-heading font-semibold">Aucune demande</h3>
                    <p className="text-sm text-muted-foreground mt-1">Les nouvelles demandes apparaîtront ici.</p>
                  </div>
                ) : (
                  <Table data-testid="admin-quotes-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Société</TableHead>
                        <TableHead>Produit</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredQuotes.map((q) => (
                        <TableRow key={q.id} className="cursor-pointer" onClick={() => navigate(`/admin/devis/${q.id}`)} data-testid={`admin-quote-row-${q.id}`}>
                          <TableCell className="font-mono text-xs whitespace-nowrap">{formatDate(q.created_at)}</TableCell>
                          <TableCell>
                            <div className="font-medium">{q.name}</div>
                            <div className="text-xs text-muted-foreground">{q.email}</div>
                          </TableCell>
                          <TableCell className="text-sm">{q.company || '—'}</TableCell>
                          <TableCell className="text-sm max-w-[240px]"><div className="truncate">{q.product_name || 'Demande générale'}</div></TableCell>
                          <TableCell>
                            <Badge variant="outline" className={STATUS_COLOR[q.status]} data-testid={`admin-quote-status-${q.id}`}>{STATUS_LABEL[q.status] || q.status}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteQuote(q.id); }} aria-label="Supprimer" className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="contacts">
              <Card className="mt-4 rounded-2xl border-border overflow-hidden">
                {loading ? (
                  <div className="p-6 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
                ) : contacts.length === 0 ? (
                  <div className="p-12 text-center">
                    <h3 className="font-heading font-semibold">Aucun message</h3>
                    <p className="text-sm text-muted-foreground mt-1">Les messages reçus via /contact apparaîtront ici.</p>
                  </div>
                ) : (
                  <Table data-testid="admin-contacts-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Expéditeur</TableHead>
                        <TableHead>Sujet</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contacts.map((m) => (
                        <TableRow key={m.id} data-testid={`admin-contact-row-${m.id}`}>
                          <TableCell className="font-mono text-xs whitespace-nowrap">{formatDate(m.created_at)}</TableCell>
                          <TableCell>
                            <div className="font-medium">{m.name}</div>
                            <a href={`mailto:${m.email}`} className="text-xs text-muted-foreground hover:text-foreground">{m.email}</a>
                          </TableCell>
                          <TableCell className="text-sm">{m.subject || '—'}</TableCell>
                          <TableCell className="max-w-[360px]"><div className="text-sm text-muted-foreground line-clamp-2">{m.message}</div></TableCell>
                          <TableCell className="text-right">
                            <a href={`mailto:${m.email}?subject=Re%3A%20${encodeURIComponent(m.subject || 'Votre message')}`} className="inline-flex">
                              <Button size="sm" variant="ghost" data-testid={`admin-contact-reply-${m.id}`}><Mail className="w-4 h-4 mr-1" />Répondre</Button>
                            </a>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteContact(m.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
